import React from "react";

/** One selectable technique. The list itself lives in `../constants/sports`. */
export type SportOption = {
  id: number;
  label: string;
  group: string;
  emoji: string;
  available: boolean;
  /**
   * Why this technique cannot be picked right now, shown in place of the group. A caller that
   * would otherwise drop an option from the list sets this instead, so the technique stays
   * visible and the reason is on screen rather than implied by an absence.
   */
  note?: string;
};

/** A technique the clip looks like, with the short reason shown under its name. */
export type SuggestedSport = { id: number; why: string };

/**
 * The technique picker, shared by the upload screen and the session page's "change technique"
 * (owner, 2026-09-20: "reuse the current sport list frontend we have on the upload screen").
 * One component, so a technique looks and reads the same wherever it is chosen and the two
 * screens cannot drift apart as sports are added.
 *
 * `options` lets a caller narrow the list — the session page offers only techniques the upload's
 * existing frames can serve — while the rendering stays identical.
 */
export function SportPicker({
  options,
  selectedId,
  onSelect,
  disabled = false,
  testIdPrefix,
  suggested,
}: {
  options: SportOption[];
  selectedId: number | null;
  onSelect: (sportId: number) => void;
  disabled?: boolean;
  /** When set, each option gets `data-testid={`${testIdPrefix}-${id}`}`. */
  testIdPrefix?: string;
  /**
   * A shortlist shown ABOVE the full list: the techniques the clip looks most like, each with the
   * reason in place of its group. The full list underneath is unchanged, and a suggestion is never
   * a selection: the user still picks. Omitted or empty = the plain list, exactly as before.
   */
  suggested?: SuggestedSport[];
}) {
  const option = (s: SportOption, key: string, subtitle: string, emphasised: boolean, testId?: string) => {
    const selected = selectedId === s.id;
    return (
      <button
        key={key}
        type="button"
        disabled={!s.available || disabled}
        data-testid={testId}
        onClick={() => {
          if (!s.available || disabled) return;
          onSelect(s.id);
        }}
        className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition
          ${selected
            ? "border-orange-500 bg-orange-50 ring-1 ring-orange-200"
            : "border-slate-200 hover:border-slate-300 bg-white"}
          ${!s.available || disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span className="text-xl">{s.emoji}</span>
        <span className="flex-1">
          <span className="block text-sm font-medium text-slate-800">{s.label}</span>
          <span className={`block text-xs ${emphasised ? "text-amber-700" : "text-slate-500"}`}>{subtitle}</span>
        </span>
      </button>
    );
  };

  const shortlist = (suggested ?? [])
    .map((g) => ({ why: g.why, option: options.find((o) => o.id === g.id && o.available) }))
    .filter((g): g is { why: string; option: SportOption } => g.option != null);

  const fullList = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={suggested ? "technique-all" : undefined}>
      {options.map((s) =>
        option(s, String(s.id), s.note ?? s.group, !!s.note, testIdPrefix ? `${testIdPrefix}-${s.id}` : undefined),
      )}
    </div>
  );
  if (shortlist.length === 0) return fullList;

  return (
    <div className="space-y-3">
      <div data-testid="technique-suggestions">
        <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">Looks like</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {shortlist.map((g) => option(g.option, `suggested-${g.option.id}`, g.why, false))}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">All techniques</div>
        {fullList}
      </div>
    </div>
  );
}

export default SportPicker;
